import React from 'react';
import HeroPlaceholder from '../components/HeroPlaceholder';
import FaqAccordion from '../components/FaqAccordion';
import CtaBand from '../components/CtaBand';
import SEO from '../components/SEO';
import AeoCallout from '../components/AeoCallout';
import RelatedBand from '../components/RelatedBand';
import { HERO_MEDIA } from '../config/media';
import { INFRASTRUCTURE_HUB, INFRASTRUCTURE_FAQS } from '../data/copy';
import { usePageContent } from '../lib/useContent';
import {
    orgJsonLd,
    breadcrumbJsonLd,
    faqJsonLd,
} from '../lib/seo';

// Frozen inline fallback - matches the current page JSX verbatim.
const INFRA_FALLBACK = {
    hero: {
        eyebrow: INFRASTRUCTURE_HUB.eyebrow,
        headline: INFRASTRUCTURE_HUB.title,
        body: 'Engineered ground protection for the projects that move Western Canada - pipeline, power, wind, oil & gas, and utility.',
        image_url: HERO_MEDIA.cranePad.src,
    },
    applications: {
        items: INFRASTRUCTURE_HUB.applications.map((a) => ({ title: a.head, body: a.body })),
    },
    faq: { items: INFRASTRUCTURE_FAQS },
};

export default function InfrastructureProjects() {
    const content = usePageContent('projects.infrastructure', INFRA_FALLBACK);
    const hero = content.hero || INFRA_FALLBACK.hero;
    const applications = (content.applications?.items?.length ? content.applications.items : INFRA_FALLBACK.applications.items);
    const faqs = (content.faq?.items?.length ? content.faq.items : INFRA_FALLBACK.faq.items);

    return (
        <>
            <SEO
                title="Infrastructure Projects - Pipeline, Power, Wind, Oil & Gas"
                description="Centurion ground protection matting protects underground utilities, stabilizes terrain, and boosts site safety on infrastructure projects across Western Canada - pipeline, power & transmission, wind, oil & gas, and utility work."
                cmsSlug="seo.projects.infrastructure"
                path="/projects/infrastructure"
                image={HERO_MEDIA.cranePad.src}
                jsonLd={[
                    orgJsonLd(),
                    breadcrumbJsonLd([
                        { name: 'Home', path: '/' },
                        { name: 'Projects', path: '/projects' },
                        { name: 'Infrastructure', path: '/projects/infrastructure' },
                    ]),
                    faqJsonLd(faqs),
                ]}
            />

            <HeroPlaceholder
                mediaKey="cranePad"
                imageUrl={hero.image_url}
                eyebrow={hero.eyebrow}
                title={hero.headline}
                subtitle={hero.body}
            />

            <section className="mx-auto max-w-4xl px-6 py-20 md:py-28">
                <span className="eyebrow">Overview</span>
                <p className="mt-8 font-sans text-lg leading-relaxed text-brand-text">
                    {INFRASTRUCTURE_HUB.intro}
                </p>

                <div className="mt-12 grid gap-4 md:grid-cols-2">
                    <AeoCallout
                        testId="aeo-infra-why-timber"
                        q="Why ground protection matting on infrastructure work?"
                        a="Ground protection mats spread heavy equipment loads across the ground, protect underground utilities, reduce environmental disturbance, and let crews work continuously through soft, frozen and sensitive terrain. Centurion mats are milled in BC from kiln-dried structural timber and engineered for repeated re-deploy across pipeline, power, wind, oil & gas and utility projects."
                    />
                    <AeoCallout
                        testId="aeo-infra-buy-vs-rent"
                        q="Is Centurion the right fit for an infrastructure matting program?"
                        a="Yes - Centurion is a manufacturer, not a lessor. Buy direct from the mill when the campaign is long, the route is repeatable, or the matting becomes part of your fleet. Manufacturer Direct from our Chemainus, BC mill gives you the lowest delivered cost, custom-spec capability, and predictable lead times tied to our own schedule."
                    />
                </div>
            </section>

            <section className="border-y border-brand-hairline bg-brand-surface" data-eyebrow-tone="blue">
                <div className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
                    <span className="eyebrow">Applications</span>
                    <h2 className="h-display mt-6 max-w-3xl text-3xl sm:text-4xl">
                        Six places Centurion ground protection matting earns its keep.
                    </h2>
                    <div
                        className="mt-12 grid gap-px bg-brand-hairline md:grid-cols-2 lg:grid-cols-3"
                        data-testid="infra-applications"
                    >
                        {applications.map((a, i) => (
                            <div
                                key={a.title}
                                className="bg-brand-surface p-6 md:p-8"
                                data-testid={`infra-application-${i}`}
                            >
                                <span className="font-display text-xs font-bold tabular-nums tracking-[0.2em] text-brand-gold">
                                    0{i + 1}
                                </span>
                                <h3 className="mt-5 font-display text-lg font-bold uppercase tracking-tight text-brand-text">
                                    {a.title}
                                </h3>
                                <p className="mt-3 font-sans text-sm leading-relaxed text-brand-muted">
                                    {a.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-4xl px-6 py-20 md:py-28">
                <span className="eyebrow">FAQ</span>
                <h2 className="h-display mt-6 text-3xl sm:text-4xl">
                    Common questions about infrastructure matting.
                </h2>
                <div className="mt-10">
                    <FaqAccordion faqs={faqs} />
                </div>
            </section>

            <RelatedBand
                heading="Adjacent project hubs."
                items={[
                    { to: '/projects', eyebrow: 'Portfolio', title: 'All Projects', body: 'Pipeline, infrastructure, wind, and partnerships in one view.' },
                    { to: '/projects/oil-gas-pipeline', eyebrow: 'Pipeline', title: 'Oil & Gas / Pipeline', body: 'Well-site access, mainline corridors, tank-farm staging.' },
                    { to: '/projects/renewables', eyebrow: 'Renewables', title: 'Wind & Renewables', body: 'Crane pads and access roads for wind farm and solar construction.' },
                ]}
            />

            <CtaBand
                eyebrow="Manufacturer Direct"
                title="Spec the matting package for your next infrastructure build."
                body="Pipeline, power, wind, oil & gas, or utility - Manufacturer Direct from our Chemainus, BC mill."
                cta={{ to: '/quote', label: 'Request a Quote' }}
                secondary={{ to: '/contact', label: 'Talk to Sales' }}
            />
        </>
    );
}
